import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import type { ItemType, TripItem, TripWithItems } from '@shared/types';
import type { PlaceCandidate } from '@shared/types';
import { ITEM_TYPES, ITEM_TYPE_LABELS, TYPES_WITHOUT_IMAGES } from '@shared/types';
import { api, ApiError } from '../lib/api';
import { ItemCard } from '../components/ItemCard';
import { ItemEditor } from '../components/ItemEditor';
import { ImportPanel } from '../components/ImportPanel';
import { PlacePicker } from '../components/PlacePicker';

const TYPE_PICKER: Array<{ type: ItemType; label: string }> = [
  { type: 'lodging', label: 'Stay' },
  { type: 'restaurant', label: 'Eat' },
  { type: 'activity', label: 'Do' },
  { type: 'tip', label: 'Tip' },
];

export function EditTripPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState<TripWithItems | null>(null);
  const [type, setType] = useState<ItemType>('lodging');
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [picking, setPicking] = useState(false);
  const [place, setPlace] = useState<{ place: PlaceCandidate; photoRef: string | null } | null>(null);
  const urlInput = useRef<HTMLInputElement>(null);

  // Bias place search toward the trip's own coordinates when we have any, so
  // "The Baths" resolves near this trip rather than anywhere on earth.
  const bias = (() => {
    const withCoords = trip?.items.find((i) => i.lat !== null && i.lng !== null);
    return withCoords ? { lat: withCoords.lat!, lng: withCoords.lng! } : null;
  })();

  const load = useCallback(async () => {
    try {
      setTrip(await api.getTrip(id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load this trip.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function addItem(e: React.FormEvent) {
    e.preventDefault();
    if (!trip || (!url.trim() && !name.trim() && !note.trim() && !place)) return;

    setBusy(true);
    setError(null);

    // Optimistic placeholder so the card appears the instant you paste,
    // rather than after the unfurl round-trip.
    const optimistic: TripItem = {
      id: `pending-${Date.now()}`,
      tripId: trip.id,
      type,
      url: url.trim() || null,
      ogTitle: name.trim() || (place?.place.name ?? null),
      ogImage: place?.place.photoUrl ?? null,
      ogDescription: null, ogSiteName: null,
      note: note.trim() || null,
      sortOrder: trip.items.length,
      lat: place?.place.lat ?? null, lng: place?.place.lng ?? null,
      unfurlStatus: url.trim() ? 'pending' : 'none',
      placeId: place?.place.placeId ?? null,
      placeName: place?.place.name ?? null,
      placeAddress: place?.place.address ?? null,
      mapUrl: place?.place.mapUrl ?? null,
      imageSource: place ? 'places' : null,
    };
    setTrip({ ...trip, items: [...trip.items, optimistic] });
    setUrl('');
    setName('');
    setNote('');
    urlInput.current?.focus();

    try {
      const saved = await api.addItem(trip.id, {
        type,
        url: optimistic.url,
        note: optimistic.note,
        // A written name beats whatever the link unfurls to.
        ...(name.trim() ? { ogTitle: name.trim() } : {}),
        ...(place?.place.placeId
          ? {
              placeId: place.place.placeId,
              placeName: place.place.name,
              placeAddress: place.place.address,
              mapUrl: place.place.mapUrl,
              lat: place.place.lat,
              lng: place.place.lng,
              // Without a written name or a link, the place names the card.
              ...(optimistic.url || name.trim() ? {} : { ogTitle: place.place.name }),
            }
          : {}),
        // A Places photo arrives as a photo reference; a manually pasted image
        // arrives as a plain URL. Both must be forwarded, or the image the user
        // just picked is silently dropped on save.
        ...(place?.photoRef
          ? { placePhotoRef: place.photoRef }
          : place?.place.photoUrl
            ? { imageUrl: place.place.photoUrl }
            : {}),
      });
      setPlace(null);
      setTrip((prev) =>
        prev
          ? { ...prev, items: prev.items.map((i) => (i.id === optimistic.id ? saved : i)) }
          : prev,
      );
    } catch (err) {
      setTrip((prev) =>
        prev ? { ...prev, items: prev.items.filter((i) => i.id !== optimistic.id) } : prev,
      );
      setError(err instanceof ApiError ? err.message : 'Could not add that.');
    } finally {
      setBusy(false);
    }
  }

  async function refreshWhilePending() {
    await api.enrichTrip(id).catch(() => {});
    for (let i = 0; i < 5; i++) {
      await new Promise((r) => setTimeout(r, 2500));
      const fresh = await api.getTrip(id).catch(() => null);
      if (!fresh) return;
      setTrip(fresh);
      if (!fresh.items.some((it) => it.unfurlStatus === 'pending')) return;
    }
  }

  async function removeItem(itemId: string) {
    if (!trip) return;
    setTrip({ ...trip, items: trip.items.filter((i) => i.id !== itemId) });
    await api.deleteItem(trip.id, itemId).catch(() => void load());
  }

  async function onDrop(targetId: string) {
    if (!trip || !dragId || dragId === targetId) return;
    const items = [...trip.items];
    const from = items.findIndex((i) => i.id === dragId);
    const to = items.findIndex((i) => i.id === targetId);
    if (from < 0 || to < 0) return;

    const [moved] = items.splice(from, 1);
    items.splice(to, 0, moved!);
    setTrip({ ...trip, items });
    setDragId(null);
    await api.reorderItems(trip.id, items.map((i) => i.id)).catch(() => void load());
  }

  if (error && !trip) {
    return (
      <main className="mx-auto max-w-2xl px-5 py-20 text-center">
        <p className="font-body text-muted">{error}</p>
        <Link to="/" className="btn-quiet mt-4 inline-block">
          Back to your trips
        </Link>
      </main>
    );
  }
  if (!trip) return <main className="mx-auto max-w-2xl px-5 py-16" />;

  return (
    <main className="mx-auto max-w-2xl px-5 py-10 pb-24">
      <div className="flex items-baseline justify-between gap-4">
        <h1 className="font-display text-title text-ink">{trip.title}</h1>
        <Link to={`/t/${trip.slug}`} className="btn-quiet shrink-0 !text-meta">
          View trip page
        </Link>
      </div>

      {importing ? (
        <div className="mt-7">
          <ImportPanel
            tripId={trip.id}
            onImported={(items) => {
              setImporting(false);
              setTrip((prev) => (prev ? { ...prev, items: [...prev.items, ...items] } : prev));
              void refreshWhilePending();
            }}
            onCancel={() => setImporting(false)}
          />
        </div>
      ) : (
        <button onClick={() => setImporting(true)} className="btn-quiet mt-7 !text-meta">
          Paste a bunch at once
        </button>
      )}

      <form onSubmit={addItem} className="mt-3 rounded-note bg-raised p-4">
        <div className="flex flex-wrap gap-1.5">
          {TYPE_PICKER.map((t) => (
            <button
              key={t.type}
              type="button"
              onClick={() => setType(t.type)}
              className={`font-util text-meta font-medium rounded-btn px-3 py-1.5 transition-colors ${
                type === t.type ? 'bg-accent text-surface' : 'bg-surface text-muted hover:text-ink'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <input
          ref={urlInput}
          className="field mt-3"
          placeholder="Paste a link"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          inputMode="url"
          autoComplete="off"
        />
        <input
          className="field mt-2"
          placeholder={
            type === 'tip'
              ? 'Give the tip a heading (optional)'
              : url.trim()
                ? 'Name it yourself, or let the link do it'
                : 'Name the place'
          }
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="off"
        />
        <textarea
          className="field mt-2 min-h-[4.5rem] resize-y font-body"
          placeholder={
            type === 'tip'
              ? 'What should they know?'
              : 'What would you tell a friend about this place?'
          }
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        {!TYPES_WITHOUT_IMAGES.includes(type) && (
          <div className="mt-2">
            {place ? (
              <div className="flex items-center gap-2">
                {place.place.photoUrl && (
                  <img src={place.place.photoUrl} alt="" className="h-12 w-12 rounded-img object-cover" />
                )}
                <span className="min-w-0 flex-1 font-util text-meta text-ink">
                  {place.place.name || 'Image selected'}
                  {place.place.address && (
                    <span className="block truncate text-muted">{place.place.address}</span>
                  )}
                </span>
                <button type="button" onClick={() => setPlace(null)} className="btn-quiet !py-1 !text-meta">
                  Clear
                </button>
              </div>
            ) : picking ? (
              <PlacePicker
                bias={bias}
                onPick={(p, ref) => {
                  setPlace({ place: p, photoRef: ref });
                  setPicking(false);
                }}
                onCancel={() => setPicking(false)}
              />
            ) : (
              <button type="button" onClick={() => setPicking(true)} className="btn-quiet !py-1.5 !text-meta">
                Find a photo and map link
              </button>
            )}
          </div>
        )}

        <button type="submit" disabled={busy} className="btn-primary mt-2 disabled:opacity-60">
          {busy ? 'Adding…' : 'Add to trip'}
        </button>
        {error && <p className="mt-2 font-util text-meta text-[#B4462F]">{error}</p>}
      </form>

      {ITEM_TYPES.map((t) => {
        const items = trip.items.filter((i) => i.type === t);
        if (!items.length) return null;
        return (
          <section key={t} className="mt-9">
            <h2 className="font-util text-section font-semibold text-ink/70">
              {ITEM_TYPE_LABELS[t].plural}
            </h2>
            <div className="mt-1 divide-y divide-ink/8">
              {items.map((item) =>
                editingId === item.id ? (
                  <div key={item.id} className="py-3">
                    <ItemEditor
                      item={item}
                      tripId={trip.id}
                      bias={bias}
                      onSaved={(updated) => {
                        setTrip((prev) =>
                          prev
                            ? { ...prev, items: prev.items.map((i) => (i.id === updated.id ? updated : i)) }
                            : prev,
                        );
                        setEditingId(null);
                      }}
                      onCancel={() => setEditingId(null)}
                    />
                  </div>
                ) : (
                  <div
                    key={item.id}
                    draggable
                    onDragStart={() => setDragId(item.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onDrop(item.id)}
                    className={dragId === item.id ? 'opacity-50' : undefined}
                  >
                    <ItemCard
                      item={item}
                      onEdit={() => setEditingId(item.id)}
                      onDelete={() => removeItem(item.id)}
                    />
                  </div>
                ),
              )}
            </div>
          </section>
        );
      })}

      <button
        onClick={async () => {
          if (!confirm('Delete this trip and everything in it?')) return;
          await api.deleteTrip(trip.id);
          navigate('/');
        }}
        className="btn-quiet mt-12 !text-meta text-muted"
      >
        Delete this trip
      </button>
    </main>
  );
}
